import { objectWithoutProperties as _objectWithoutProperties, extends as _extends } from './_virtual/_rollupPluginBabelHelpers.js';
import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { closeIcon } from '@lululemon/ecom-pattern-library-icons';
import { FocusTrap } from '@lululemon/ecom-pattern-library-utils-focus-trap';
import { usePortal, useHandleEscapeKey, usePreventBodyScroll, useAriaHideSiblings } from '@lululemon/ecom-pattern-library-utils-hooks';
import { calculateStyles } from './util.js';
import { Icon } from '@lululemon/ecom-pattern-library-icon';
import styles from './index.module.scss.js';

var _excluded = ["messages", "children", "visible", "placement", "width", "height", "onRequestClose", "primaryContentSelector", "openFocusRef", "closeFocusRef", "onClose", "cancelProps", "classes"];
var Drawer = _ref => {
  var {
      messages,
      children,
      visible,
      placement,
      width,
      height,
      onRequestClose,
      primaryContentSelector,
      openFocusRef,
      closeFocusRef,
      onClose,
      cancelProps,
      classes
    } = _ref,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  var portalContainerRef = usePortal();
  useHandleEscapeKey(visible, onRequestClose);
  usePreventBodyScroll(visible);
  useAriaHideSiblings(visible, primaryContentSelector, portalContainerRef.current);
  var drawerInlineStyles = calculateStyles(placement, width, height, visible);
  var drawerRef = useRef(null);
  var [didMount, setDidMount] = useState(false);
  useEffect(() => {
    setDidMount(true);
  }, []);

  // onClose callback
  useEffect(() => {
    return () => {
      if (visible && onClose) {
        onClose();
      }
    };
  }, [visible, onClose]);
  if (!didMount) {
    return null;
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/createPortal( /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: cn({
      [styles.backdrop]: visible
    }),
    "data-testid": "backdrop",
    onClick: ev => onRequestClose(ev)
  }), /*#__PURE__*/React.createElement("div", {
    style: drawerInlineStyles,
    className: cn(styles.drawerContainer, classes === null || classes === void 0 ? void 0 : classes.container, {
      [styles.top]: (placement === null || placement === void 0 ? void 0 : placement.toLowerCase()) === 'top',
      [styles.bottom]: (placement === null || placement === void 0 ? void 0 : placement.toLowerCase()) === 'bottom',
      [styles.left]: (placement === null || placement === void 0 ? void 0 : placement.toLowerCase()) === 'left',
      [styles.right]: (placement === null || placement === void 0 ? void 0 : placement.toLowerCase()) === 'right'
    }),
    "data-testid": "drawer-container"
  }, visible ? /*#__PURE__*/React.createElement(FocusTrap, {
    openFocusRef: openFocusRef,
    closeFocusRef: closeFocusRef,
    childRef: drawerRef
  }, /*#__PURE__*/React.createElement("div", _extends({
    "aria-modal": "true",
    role: "dialog",
    className: styles.drawer,
    ref: drawerRef
  }, otherProps), /*#__PURE__*/React.createElement("button", _extends({
    type: "button",
    "aria-label": messages.close,
    className: styles.closeButton,
    onClick: onRequestClose
  }, cancelProps), /*#__PURE__*/React.createElement(Icon, {
    content: closeIcon
  })), /*#__PURE__*/React.createElement("div", {
    className: styles.drawerContent
  }, children))) : null)), portalContainerRef.current));
};
var propTypes = {
  children: PropTypes.node.isRequired,
  /** Callback fired when the drawer closes */
  onClose: PropTypes.func,
  /** Flag indicating whether the Drawer should display or not */
  visible: PropTypes.bool,
  /** Used to determine which placement the Drawer
   * will come out of
   */
  placement: PropTypes.oneOf(['left', 'right', 'top', 'bottom']),
  /** Used to overwite the default width of the Drawer
   * only applies to Left and Right max width is set at 40%
   */
  width: PropTypes.string,
  /** Used to overwite the default height of the Drawer
   * Only applies to Top and Bottom max height is set at 80%
   */
  height: PropTypes.string,
  /** Callback fired when a user attempts to close the drawer.
   * Use this to update the `visible` prop. */
  onRequestClose: PropTypes.func,
  /** A CSS selector to select your primary page content.
   * This content is hidden from screen readers while the
   * drawer is open.
   */
  primaryContentSelector: PropTypes.string,
  /** Element ref for the element that should gain focus when
   * the drawer opens. Defaults to the first tabbable element
   * in the drawer (the close button, usually).
   */
  openFocusRef: PropTypes.shape({
    current: PropTypes.object
  }),
  /** Element ref for the element that should gain focus when
   * the drawer closes. Defaults to the last focused element
   * (the button you clicked to open the modal, typically).
   */
  closeFocusRef: PropTypes.shape({
    current: PropTypes.object
  }),
  /** Optional class to overwrite the default container styles
   */
  classes: PropTypes.shape({
    container: PropTypes.string
  }),
  // eslint-disable-next-line react/forbid-prop-types
  cancelProps: PropTypes.object,
  messages: PropTypes.shape({
    /** Label for the close button */
    close: PropTypes.string.isRequired
  }).isRequired
};
var defaultProps = {
  visible: false,
  placement: 'right',
  width: '20%',
  height: '20%',
  onRequestClose: undefined,
  primaryContentSelector: 'body > *',
  openFocusRef: undefined,
  closeFocusRef: undefined,
  cancelProps: null,
  onClose: undefined,
  classes: {}
};
Drawer.propTypes = propTypes;
Drawer.defaultProps = defaultProps;

export { Drawer, defaultProps, propTypes };
//# sourceMappingURL=index.js.map
